import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createTask,
  taskGroups,
  taskUsers,
} from "@/store/tasksApi/tasksThunk";
import toast from "react-hot-toast";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Upload,
  List,
  Space,
} from "antd";
import { Link } from "react-router-dom";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/uz-latn";

dayjs.locale("uz-latn");

const { TextArea } = Input;

const TasksCreate = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { users = [], groups = [] } = useSelector((state) => state.tasksApi);
  const [steps, setSteps] = useState([]);
  const [newStep, setNewStep] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);

  useEffect(() => {
    dispatch(taskUsers());
    dispatch(taskGroups());
  }, [dispatch]);

  const userOptions = users.map((u) => ({
    value: u.id,
    label: `${u.surname} ${u.first_name} ${u.patronymic}`,
  }));

  const groupOptions = groups.map((g) => ({
    value: g.id,
    label: g.name,
  }));

  const addStep = () => {
    if (newStep.trim()) {
      setSteps((prev) => [...prev, { title: newStep.trim() }]);
      setNewStep("");
    }
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (values) => {
    const fd = new FormData();

    fd.append("title", values.title);
    fd.append("description", values.description);
    fd.append("deadline", values.deadline.format("YYYY-MM-DD HH:mm:ss"));
    fd.append("steps_required", String(values.steps_required || false));

    if (values.assigned_user_ids?.length)
      fd.append("assigned_user_ids", JSON.stringify(values.assigned_user_ids));

    if (values.assigned_group_ids?.length)
      fd.append("assigned_group_ids", JSON.stringify(values.assigned_group_ids));

    if (values.approver_id)
      fd.append("approver_id", String(values.approver_id));

    if (steps.length) fd.append("steps", JSON.stringify(steps));

    if (imageFile) fd.append("image", imageFile);
    if (attachedFile) fd.append("file", attachedFile);

    dispatch(createTask(fd)).then((response) => {
      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success("Topshiriq muvaffaqiyatli yaratildi!");
        form.resetFields();
        setSteps([]);
        setImageFile(null);
        setAttachedFile(null);
      } else {
        toast.error(
          response?.payload?.response?.data?.message ||
            "Topshiriq yaratishda xatolik"
        );
      }
    });
  };

  return (
    <div className="p-3">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item>
          <Link to="/">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Topshiriq yaratish</Breadcrumb.Item>
      </Breadcrumb>

      <Card bordered={false}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            steps_required: false,
            assigned_user_ids: [],
            assigned_group_ids: [],
          }}
        >
          <Row gutter={16}>
            {/* Title */}
            <Col span={12}>
              <Form.Item
                label="Sarlavha"
                name="title"
                rules={[
                  { required: true, message: "Topshiriq sarlavhasini kiriting!" },
                ]}
              >
                <Input
                  size="middle"
                  placeholder="Masalan: Hisobot tayyorlash"
                />
              </Form.Item>
            </Col>

            {/* Deadline */}
            <Col span={12}>
              <Form.Item
                label="Topshiriq muddati"
                name="deadline"
                rules={[
                  {
                    required: true,
                    message: "Topshiriq muddatini tanlang!",
                  },
                ]}
              >
                <DatePicker
                  showTime
                  size="middle"
                  className="w-100"
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Sana va vaqtni tanlang"
                />
              </Form.Item>
            </Col>

            {/* Description */}
            <Col span={24}>
              <Form.Item
                label="Tavsif"
                name="description"
                rules={[
                  { required: true, message: "Topshiriq tavsifini kiriting!" },
                ]}
              >
                <TextArea
                  rows={3}
                  size="middle"
                  placeholder="Topshiriq haqida batafsil yozing..."
                />
              </Form.Item>
            </Col>

            {/* Users */}
            <Col span={12}>
              <Form.Item
                label="Xodimlar"
                name="assigned_user_ids"
                rules={[
                  {
                    required: true,
                    message: "Kamida bitta xodim tanlang!",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  size="middle"
                  options={userOptions}
                  placeholder="Xodimlarni tanlang"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            {/* Groups */}
            <Col span={12}>
              <Form.Item label="Guruh" name="assigned_group_ids">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  size="middle"
                  options={groupOptions}
                  placeholder="Guruhni tanlang"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            {/* Approver */}
            <Col span={12}>
              <Form.Item label="Tasdiqlovchi" name="approver_id">
                <Select
                  allowClear
                  showSearch
                  size="middle"
                  options={userOptions}
                  placeholder="Tasdiqlovchini tanlang"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            {/* Steps Required */}
            <Col span={12}>
              <Form.Item
                label="Ijrochi bosqichlarni kiritishi majburiymi?"
                name="steps_required"
                valuePropName="checked"
              >
                <Switch size="middle" />
              </Form.Item>
            </Col>

            {/* Steps */}
            <Col span={24}>
              <Form.Item label="Bosqichlar">
                <Input.Group compact>
                  <Input
                    size="middle"
                    style={{ width: "calc(100% - 50px)" }}
                    placeholder="Bosqich nomini kiriting..."
                    value={newStep}
                    onChange={(e) => setNewStep(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addStep();
                      }
                    }}
                  />
                  <Button
                    size="middle"
                    type="primary"
                    onClick={addStep}
                    icon={<PlusOutlined />}
                  />
                </Input.Group>

                <List
                  bordered
                  dataSource={steps}
                  style={{ marginTop: 10 }}
                  renderItem={(s, idx) => (
                    <List.Item
                      actions={[
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => removeStep(idx)}
                        />,
                      ]}
                    >
                      {s.title}
                    </List.Item>
                  )}
                />
              </Form.Item>
            </Col>

            {/* Image Upload */}
            <Col span={12}>
              <Form.Item label="Rasm yuklash">
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  accept="image/*"
                  onChange={(info) => {
                    const file = info.fileList[0]?.originFileObj || null;
                    setImageFile(file);
                  }}
                  onRemove={() => setImageFile(null)}
                  fileList={
                    imageFile
                      ? [
                          {
                            uid: "-1",
                            name: imageFile.name,
                            status: "done",
                          },
                        ]
                      : []
                  }
                >
                  <Button icon={<UploadOutlined />}>Rasm tanlang</Button>
                </Upload>
              </Form.Item>
            </Col>

            {/* File Upload */}
            <Col span={12}>
              <Form.Item label="Fayl yuklash">
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  accept="*/*"
                  onChange={(info) => {
                    const file = info.fileList[0]?.originFileObj || null;
                    setAttachedFile(file);
                  }}
                  onRemove={() => setAttachedFile(null)}
                  fileList={
                    attachedFile
                      ? [
                          {
                            uid: "-2",
                            name: attachedFile.name,
                            status: "done",
                          },
                        ]
                      : []
                  }
                >
                  <Button icon={<UploadOutlined />}>Fayl tanlang</Button>
                </Upload>
              </Form.Item>
            </Col>

            {/* Submit */}
            <Col span={24} style={{ textAlign: "right" }}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  icon={<PlusOutlined />}
                >
                  Topshiriq yaratish
                </Button>
                <Button
                  size="middle"
                  htmlType="button"
                  onClick={() => form.resetFields()}
                >
                  Tozalash
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default TasksCreate;
